import { useState, useEffect } from 'react';
import { FileText, Calendar, DollarSign, Users, Download, Printer, Loader2, AlertCircle } from 'lucide-react';
import { getDocumentText, isWordApiAvailable } from '../utils/wordApi';
import { generateContractSummary, type ContractSummaryResult } from '../utils/contractSummary';
import { resolveContractContext, type ContractContext } from '../utils/contractResolver';

export function ContractSummary() {
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
  const [summary, setSummary] = useState<ContractSummaryResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentContract, setCurrentContract] = useState<ContractContext | null>(null);

  useEffect(() => {
    resolveContractContext().then((ctx) => {
      if (ctx) setCurrentContract(ctx);
    }).catch((err) => console.error('Error loading contract context:', err));
  }, []);

  const handleGenerateSummary = async (): Promise<void> => {
    if (!isWordApiAvailable()) {
      setError('Word API is not available. Please ensure you are running this add-in in Microsoft Word.');
      return;
    }

    const tenantId = currentContract?.tenantId;
    if (!tenantId) {
      setError('Contract context not available. Please open this document from EzyLex CLM.');
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setSummary(null);

    try {
      // Get document text
      const documentText = await getDocumentText();

      if (!documentText || documentText.trim().length === 0) {
        setError('Document appears to be empty. Please add some content to summarize.');
        setIsAnalyzing(false);
        return;
      }

      // Generate summary using OpenAI
      const result = await generateContractSummary(documentText, tenantId);
      setSummary(result);
    } catch (err) {
      console.error('Error generating summary:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate contract summary. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div style={{ fontSize: '13px' }}>
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div
            className="p-1.5 rounded-lg grid place-items-center"
            style={{ background: 'var(--brand)' }}
          >
            <FileText className="w-4 h-4 text-white" />
          </div>
          <div>
            <h1 className="text-[15px] font-bold text-[var(--ink)]">Contract Summary</h1>
            <p className="text-[11.5px] text-[var(--ink-3)]">AI-generated contract overview</p>
          </div>
        </div>

        {!summary && (
          <div className="ez-card p-4 mb-3">
            <p className="text-[12px] text-[var(--ink-3)] mb-3 leading-relaxed">
              Generate a plain-language overview of this contract — parties, key
              dates, value, and a section-by-section summary.
            </p>
            <button
              onClick={handleGenerateSummary}
              disabled={isAnalyzing}
              className="w-full px-4 py-2 rounded-[var(--r-lg)] transition-colors flex items-center justify-center gap-2 text-white disabled:cursor-not-allowed"
              style={{ fontSize: '13px', background: isAnalyzing ? 'var(--ink-4)' : 'var(--brand)' }}
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Generating summary…</span>
                </>
              ) : (
                <span>Generate summary</span>
              )}
            </button>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-600" />
              <p className="text-red-800" style={{ fontSize: '12px' }}>{error}</p>
            </div>
          </div>
        )}

        {summary && (
          <>
            <div className="bg-white rounded-lg shadow-sm border border-[var(--line)] p-4 mb-4">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-[var(--ink)] mb-2">{summary.title}</h2>
                  {summary.parties.length > 0 && (
                    <p className="text-xs text-[var(--ink-3)]">
                      {summary.parties.length === 1 
                        ? `Party: ${summary.parties[0]}`
                        : `Parties: ${summary.parties.join(' and ')}`}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button className="p-2 border border-[var(--line)] rounded-lg hover:bg-[var(--bg-soft)] transition-colors" title="Download">
                    <Download className="w-4 h-4 text-[var(--ink-3)]" />
                  </button>
                  <button className="p-2 border border-[var(--line)] rounded-lg hover:bg-[var(--bg-soft)] transition-colors" title="Print">
                    <Printer className="w-4 h-4 text-[var(--ink-3)]" />
                  </button>
                </div>
              </div>
              
              {summary.metadata && (summary.metadata.contractDate || summary.metadata.expirationDate || summary.metadata.totalValue) && (
                <div className="grid grid-cols-2 gap-3">
                  {summary.metadata.contractDate && (
                    <div className="flex items-center gap-2 p-2 bg-[var(--bg-soft)] rounded-lg">
                      <Calendar className="w-4 h-4 text-[var(--brand-3)]" />
                      <div>
                        <p className="text-xs text-[var(--ink-3)]">Contract Date</p>
                        <p className="text-sm font-semibold text-[var(--ink)]">{summary.metadata.contractDate}</p>
                      </div>
                    </div>
                  )}
                  {summary.metadata.expirationDate && (
                    <div className="flex items-center gap-2 p-2 bg-[var(--bg-soft)] rounded-lg">
                      <Calendar className="w-4 h-4 text-[var(--brand-3)]" />
                      <div>
                        <p className="text-xs text-[var(--ink-3)]">Expiration Date</p>
                        <p className="text-sm font-semibold text-[var(--ink)]">{summary.metadata.expirationDate}</p>
                      </div>
                    </div>
                  )}
                  {summary.metadata.totalValue && (
                    <div className="flex items-center gap-2 p-2 bg-[var(--bg-soft)] rounded-lg">
                      <DollarSign className="w-4 h-4 text-[var(--brand-3)]" />
                      <div>
                        <p className="text-xs text-[var(--ink-3)]">Total Value</p>
                        <p className="text-sm font-semibold text-[var(--ink)]">{summary.metadata.totalValue}</p>
                      </div>
                    </div>
                  )}
                  {summary.parties.length > 0 && (
                    <div className="flex items-center gap-2 p-2 bg-[var(--bg-soft)] rounded-lg">
                      <Users className="w-4 h-4 text-[var(--brand-3)]" />
                      <div>
                        <p className="text-xs text-[var(--ink-3)]">Parties</p>
                        <p className="text-sm font-semibold text-[var(--ink)]">{summary.parties.length} {summary.parties.length === 1 ? 'Party' : 'Parties'}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Summary Sections */}
            {summary.summarySections.length > 0 && (
              <div className="space-y-3 mb-4">
                {summary.summarySections.map((section, index) => (
                  <div key={index} className="bg-white rounded-lg shadow-sm border border-[var(--line)] p-4">
                    <h3 className="font-semibold text-[var(--ink)] mb-2" style={{ fontSize: '14px' }}>
                      {section.title}
                    </h3>
                    <p className="text-[var(--ink-2)]" style={{ fontSize: '12px', lineHeight: '1.6' }}>
                      {section.content}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Key Terms */}
            {summary.keyTerms.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-[var(--line)] p-4 mb-4">
                <h3 className="font-semibold text-[var(--ink)] mb-3" style={{ fontSize: '14px' }}>Key Terms</h3>
                <ul className="space-y-2">
                  {summary.keyTerms.map((term, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand-3)] mt-1.5 flex-shrink-0" />
                      <span className="text-[var(--ink-2)]" style={{ fontSize: '12px', lineHeight: '1.5' }}>{term}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        {!isAnalyzing && !summary && !error && (
          <div className="bg-white rounded-lg shadow-sm border border-[var(--line)] p-6 text-center">
            <FileText className="w-12 h-12 text-[var(--ink-4)] mx-auto mb-3" />
            <p className="text-[var(--ink-3)]" style={{ fontSize: '13px' }}>
              Click "View Summary" to generate an AI-powered summary of your contract.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
